import React, { useMemo } from 'react'
import SavedSearchSelect from '@/shared/components/ui/Select/SavedSearchSelect'
import EditableQuery from '../EditableQuery'
import type { PartKind, QueryCriteria } from '../queryParts'

const SavedSearchSelectComponent = SavedSearchSelect as React.ComponentType<any>

interface QueryBarProps {
    customQueries: any[]
    selectedId: string
    placeholder: string
    value: string
    criteria?: QueryCriteria | null
    onChange: (text: string) => void
    onSearch?: (query: string) => void
    onSelectQuery?: (query: any) => void
    onNewSearch?: () => void
}

/** Saved-search selector on the left, the editable query sentence on a grey panel to the right. */
export default function QueryBar({
    customQueries,
    selectedId,
    placeholder,
    value,
    criteria,
    onChange,
    onSearch,
    onSelectQuery,
    onNewSearch,
}: QueryBarProps) {
    // Industries, locations... from the user's other saved searches, offered as suggestions.
    const extraSuggestions = useMemo(() => {
        const pick = (key: string) =>
            customQueries.flatMap((q) => (Array.isArray(q?.criteria?.[key]) ? q.criteria[key] : []))
        return {
            industry: pick('industries'),
            location: pick('locations'),
            size: pick('employee_ranges'),
            technology: pick('technologies'),
        } as Partial<Record<PartKind, string[]>>
    }, [customQueries])

    return (
        <div className='group w-full rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 pl-4 min-h-[52px] flex items-center overflow-hidden shadow-xs'>
            <SavedSearchSelectComponent
                searches={customQueries}
                selectedId={selectedId}
                placeholder={placeholder}
                onSelect={(query: any) => onSelectQuery?.(query)}
                onNewSearch={onNewSearch}
            />

            <div className='min-h-[52px] w-[1px] self-stretch bg-gray-200 dark:bg-gray-700 shrink-0' />

            <div className='flex min-w-0 flex-1 self-stretch items-center bg-gray-50 dark:bg-gray-800/40 px-4 py-2'>
                <EditableQuery
                    value={value}
                    onChange={onChange}
                    onSubmit={(next) => onSearch?.(next)}
                    criteria={criteria}
                    extraSuggestions={extraSuggestions}
                />
            </div>
        </div>
    )
}
